import { Switch, Button, Space, message, Modal, DatePicker, Tooltip } from "antd";
import { CalendarOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import globalIP from "@/GlobelIP";

interface Props {
  userId: number;
  initialAllowMultiple: boolean;
  initialExpiry?: string;
  fetchData: () => void;
}

const AccessEditor = ({
  userId,
  initialAllowMultiple,
  initialExpiry,
  fetchData
}: Props) => {

  // ⭐ If expiry exists → force allowMultiple false
  const hasExpiryInitially = !!initialExpiry;

  const [allowMultiple, setAllowMultiple] = useState(
    hasExpiryInitially ? false : initialAllowMultiple
  );
  const [expiry, setExpiry] = useState<Dayjs | null>(
    initialExpiry ? dayjs(initialExpiry) : null
  );
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // ----------------------------------
  // 🔁 Sync when props change
  // ----------------------------------
  useEffect(() => {
    if (initialExpiry) {
      setAllowMultiple(false);
      setExpiry(dayjs(initialExpiry));
    } else {
      setAllowMultiple(initialAllowMultiple);
      setExpiry(null);
    }
  }, [initialAllowMultiple, initialExpiry]);

  // ----------------------------------
  // 🔀 Toggle Allow Multiple
  // ----------------------------------
  const handleToggle = async (checked: boolean) => {
    setLoading(true);

    try {
      // ⭐ If enabling multiple → clear expiry
      if (checked && expiry) {
        await fetch(`${globalIP}/api/user-details/${userId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: { user_expiry_at: null } })
        });
        setExpiry(null);
      }

      await fetch(`${globalIP}/api/user/allow-multiple-devices`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, allow: checked })
      });

      setAllowMultiple(checked);
      message.success(`Multiple devices ${checked ? "enabled" : "disabled"}`);
      fetchData();

    } catch (err: any) {
      message.error("Failed to update access");
      setAllowMultiple(!checked);
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------
  // ⏰ Save Expiry
  // ----------------------------------
const updateExpiry = async () => {
  setLoading(true);

  try {
    // --------------------------------
    // ⭐ CLEAR EXPIRY
    // --------------------------------
    if (!expiry) {
      await fetch(`${globalIP}/api/user-details/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          data: { user_expiry_at: null }
        })
      });

      message.success("Expiry cleared");
      setModalOpen(false);
      fetchData();
      return;
    }

    // --------------------------------
    // ⭐ SET EXPIRY (DISABLE MULTIPLE)
    // --------------------------------
    await fetch(`${globalIP}/api/user/allow-multiple-devices`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, allow: false })
    });

    await fetch(`${globalIP}/api/user-details/${userId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        data: { user_expiry_at: expiry.toISOString() }
      })
    });

    setAllowMultiple(false);
    message.success("Expiry set successfully");
    setModalOpen(false);
    fetchData();

  } catch {
    message.error("Failed to update expiry");
  } finally {
    setLoading(false);
  }
};


  const cancelChange = () => {
    setExpiry(initialExpiry ? dayjs(initialExpiry) : null);
    setModalOpen(false);
  };

  const expiryActive = !!expiry;

  return (
    <Space>
      {/* 🔀 Allow Multiple Switch */}
      <Switch
        checked={allowMultiple}
        loading={loading}
        onChange={handleToggle}
        disabled={expiryActive} // ⭐ disable if expiry exists
        checkedChildren="Yes"
        unCheckedChildren="No"
      />

      {/* 📅 Calendar Button */}
      <Tooltip
        title={
          expiry
            ? `User expires at ${dayjs(expiry).format("DD MMM YYYY hh:mm A")}`
            : "No expiry set"
        }
      >
        <Button
          size="small"
          icon={<CalendarOutlined />}
          onClick={() => setModalOpen(true)}
          disabled={allowMultiple} // ⭐ disable if multiple enabled
        />
      </Tooltip>

      {/* ⏰ Expiry Modal */}
      <Modal
        title="Set Expiry Date"
        open={modalOpen}
        onCancel={cancelChange}
        footer={
          <Space>
            <Button onClick={cancelChange} icon={<CloseOutlined />} />
            <Button
              type="primary"
              onClick={updateExpiry}
              loading={loading}
              icon={<CheckOutlined />}
            />
          </Space>
        }
      >
        <DatePicker
          showTime={{ format: "hh:mm A" }}
          allowClear
          value={expiry}
          format="DD MMM YYYY hh:mm A"
          onChange={(date) => setExpiry(date)}
        />
      </Modal>
    </Space>
  );
};

export default AccessEditor;
